import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Presentational states for the Patient Priority Index worklist access gate
 * (P6c). Pure presentation — all decisioning lives in useWorklistAccessGate.
 * Styled with the repo's existing semantic-token conventions (see
 * auth/PpLoginModal.tsx / members/MemberResultsTable.tsx empty states) rather
 * than raw HTML.
 */

function GateShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-full min-h-0 flex flex-col items-center justify-center px-6 py-16">
      <div className="max-w-md w-full text-center rounded-2xl border border-dashed border-border bg-card/50 p-8 flex flex-col items-center gap-4">
        {children}
      </div>
    </div>
  );
}

export interface PpLoginRequiredStateProps {
  identityEmail: string | undefined;
  onLogin: () => void;
}

/** Shown when the provider holds no Provider Portal bearer yet. */
export function PpLoginRequiredState({ identityEmail, onLogin }: PpLoginRequiredStateProps) {
  return (
    <GateShell>
      <h2 className="text-lg font-bold text-foreground m-0">
        Log in to Provider Portal to view your priority worklist
      </h2>
      <p className="text-sm text-muted-foreground m-0">
        Your ranked patient worklist is powered by Provider Portal. Sign in to
        continue.
      </p>
      {identityEmail ? (
        <button
          type="button"
          onClick={onLogin}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Log in to Provider Portal
        </button>
      ) : (
        <p role="alert" className="text-sm text-destructive font-medium m-0">
          Unable to determine your sign-in identity — please refresh and try again.
        </p>
      )}
    </GateShell>
  );
}

export interface NpiRequiredStateProps {
  error: string | null;
  onSubmit: (value: string) => boolean;
}

/** Shown once a PP bearer is present but no NPI has been entered/persisted. */
export function NpiRequiredState({ error, onSubmit }: NpiRequiredStateProps) {
  const [value, setValue] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSubmit(value);
  }

  return (
    <GateShell>
      <h2 className="text-lg font-bold text-foreground m-0">
        Enter your NPI to view your priority worklist
      </h2>
      <p className="text-sm text-muted-foreground m-0">
        Your priority worklist is scoped to your National Provider Identifier
        (NPI). This is remembered on this device.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full pt-1">
        <label htmlFor="worklist-npi-input" className="flex flex-col gap-1 text-left">
          <span className="text-xs font-semibold text-foreground/80 uppercase tracking-wide">
            NPI
          </span>
          <input
            id="worklist-npi-input"
            name="npi"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            maxLength={10}
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^\d]/g, ""))}
            placeholder="10-digit NPI"
            autoFocus
            className="rounded-xl border border-input px-4 py-2.5 text-base text-foreground bg-background hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary transition-all duration-150 text-center tracking-widest"
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-destructive font-medium m-0">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={value.length !== 10}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          View worklist
        </button>
      </form>
    </GateShell>
  );
}

export interface WorklistIdentityBarProps {
  /** The NPI the worklist is CURRENTLY scoped to. */
  npi: string;
  /**
   * Every entitled provider NPI under the resolved IPA. More than one renders
   * a real switcher; exactly one renders a plain label (server-auto-linked,
   * nothing to choose); ZERO means the NPI was typed in by hand, and only then
   * is `onChangeNpi` offered.
   */
  npis: string[];
  /** Switches the worklist to a different entitled NPI IN PLACE (persists the
   *  pick per-device via accessGate.selectNpi). */
  onSelectNpi: (npi: string) => void;
  /** Clears a MANUALLY ENTERED NPI (the zero-entitled case) so the provider can
   *  type another — returns the gate to `no-npi`. Not an affordance when
   *  entitled NPIs exist: there the switcher is the way to change. */
  onChangeNpi: () => void;
  /** Every IPA the signed-in provider is entitled to. Always has at least one
   *  entry by the time this bar renders (WorklistPage only mounts it once
   *  `accessGate.status === "ready"`, which itself requires >=1 resolved
   *  IPA — see computeWorklistAccessStatus). Drives the always-visible inline
   *  switcher below, at every count. */
  ipas: string[];
  /** The IPA the worklist is CURRENTLY scoped to — one of `ipas`. */
  activeIpa: string;
  /** Switches the worklist to a different entitled IPA IN PLACE (persists the
   *  pick per-device via accessGate.selectIpa). */
  onSelectIpa: (ipa: string) => void;
}

/**
 * Single-line identity bar shown above the worklist: the active NPI and the
 * active IPA, each an inline `ScopeSwitcher` when there's more than one option.
 * Combined onto ONE row so the chrome above the list stays a single line
 * rather than two stacked banners.
 *
 * ACT-1431 — this bar is now the ONLY place either scope is chosen. Both used
 * to be full-page pickers that blocked the worklist before it ever rendered;
 * the gate auto-resolves them instead (see useWorklistAccessGate.ts), so the
 * choice lives here, beside the data it scopes, and never in front of it.
 */
export function WorklistIdentityBar({
  npi,
  npis,
  onSelectNpi,
  onChangeNpi,
  ipas,
  activeIpa,
  onSelectIpa,
}: WorklistIdentityBarProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 border-b border-border px-4 py-1.5 text-xs text-muted-foreground shrink-0">
      {npis.length > 1 ? (
        <ScopeSwitcher
          label="NPI"
          noun="NPI"
          options={npis}
          active={npi}
          onSelect={onSelectNpi}
        />
      ) : (
        <span className="inline-flex items-center gap-2">
          <span>
            NPI <span className="font-semibold text-foreground tabular-nums">{npi}</span>
          </span>
          {/* Only a hand-entered NPI (zero entitled) can be "changed" here —
              with exactly one entitled NPI the server auto-linked it and there
              is nothing to pick. */}
          {npis.length === 0 && (
            <button
              type="button"
              onClick={onChangeNpi}
              aria-label="Change NPI"
              className="rounded font-medium text-primary underline underline-offset-2 hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Change
            </button>
          )}
        </span>
      )}
      <span aria-hidden="true" className="h-3 w-px bg-border" />
      <ScopeSwitcher
        label="IPA"
        noun="IPA"
        options={ipas}
        active={activeIpa}
        onSelect={onSelectIpa}
      />
    </div>
  );
}

/**
 * Above this many options, the panel requires a query before rendering ANY of
 * them. Measured against the QA PortalManager `UserProvider` data: ~8% of users
 * are entitled to more than 100 providers in a single IPA, and the worst case is
 * 8,188. Painting that many option buttons locks the tab, so a delegated user
 * (office manager, admin) must narrow first.
 */
const SEARCH_REQUIRED_ABOVE = 100;

/** Above this many options the panel grows a search box (still rendering the
 *  list immediately — that only stops above SEARCH_REQUIRED_ABOVE). */
const SEARCH_SHOWN_ABOVE = 8;

/** Hard cap on options rendered at once, query or not. */
const MAX_RENDERED = 60;

export interface ScopeSwitcherProps {
  /** Short prefix rendered before the value, e.g. "IPA". */
  label: string;
  /** Singular noun used in aria labels and the search placeholder. */
  noun: string;
  options: string[];
  active: string;
  onSelect: (value: string) => void;
}

/**
 * Always-visible, ALWAYS-REAL inline scope switcher — a hand-rolled Popover
 * (button trigger + absolutely-positioned panel, click-outside/Escape to
 * dismiss; see components/DatePicker.tsx for the SAME pattern already used
 * elsewhere in this repo) rather than a Radix `<Select>`, which the Astrana
 * design system bans (causes page shift).
 *
 * ALWAYS an interactive dropdown — including for a single-option provider (user
 * directive 2026-07-28: "just one, but I still want a switcher"). An earlier
 * pass disabled the trigger at exactly one IPA, which rendered VISUALLY
 * IDENTICAL to the pre-switcher read-only label and so didn't deliver the ask
 * at all. Showing the one-item list is the point: it makes the worklist's
 * scope explicit and legible as a control, and it stays consistent the moment
 * a second entitlement appears (no layout/affordance change). The open list
 * marks the active value, so a single-item menu reads as confirmation of scope,
 * not as a broken empty menu.
 *
 * SCALES TO THOUSANDS. Used for the NPI as well as the IPA (ACT-1431), and a
 * delegated user (office manager, admin) can be entitled to thousands of
 * provider NPIs under one IPA — ~8% exceed 100 and the measured worst case is
 * 8,188. So the panel grows a search box past SEARCH_SHOWN_ABOVE, renders
 * nothing until narrowed past SEARCH_REQUIRED_ABOVE, and never paints more than
 * MAX_RENDERED buttons at once.
 *
 * Kept to the bar's compact `text-xs` single line and a bounded (`w-44`)
 * right-anchored panel so it fits the ~380px Chrome extension side panel
 * without overflowing.
 */
function ScopeSwitcher({ label, noun, options, active, onSelect }: ScopeSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Reopening starts from an unfiltered list — a stale query from last time
  // would look like a shorter entitlement list than the provider actually has.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const searchable = options.length > SEARCH_SHOWN_ABOVE;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, query]);
  // With thousands of options an unnarrowed list is both unrenderable and
  // useless to read, so it isn't rendered at all until the query narrows it.
  const searchRequired = options.length > SEARCH_REQUIRED_ABOVE && query.trim() === "";
  const visible = useMemo(
    () => (searchRequired ? [] : filtered.slice(0, MAX_RENDERED)),
    [searchRequired, filtered],
  );
  const truncated = !searchRequired && filtered.length > visible.length;

  return (
    <div ref={rootRef} className="relative inline-flex items-center gap-2">
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Switch ${noun} — current ${active}`}
        className="inline-flex items-center gap-1 rounded font-semibold text-primary hover:text-primary/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="tabular-nums">{active}</span>
        <ChevronDown
          className={"h-3 w-3 transition-transform " + (open ? "rotate-180" : "")}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg py-1">
          {searchable && (
            <div className="px-2 pb-1 pt-0.5">
              <input
                type="text"
                inputMode="search"
                autoComplete="off"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${noun}s…`}
                aria-label={`Search ${noun}s`}
                className="w-full rounded border border-input bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          )}
          <div role="listbox" aria-label={`Select ${noun}`} className="max-h-56 overflow-y-auto">
            {searchRequired ? (
              <p className="px-3 py-3 text-center text-xs text-muted-foreground">
                Type to search {options.length.toLocaleString()} entitled {noun}s.
              </p>
            ) : visible.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs text-muted-foreground">
                No {noun} matches “{query.trim()}”.
              </p>
            ) : (
              visible.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="option"
                  aria-selected={value === active}
                  onClick={() => {
                    onSelect(value);
                    setOpen(false);
                  }}
                  className={
                    "block w-full truncate px-3 py-1.5 text-left text-xs font-medium transition-colors " +
                    (value === active
                      ? "bg-accent text-foreground"
                      : "text-foreground hover:bg-accent")
                  }
                >
                  {value}
                </button>
              ))
            )}
          </div>
          {truncated && (
            <p className="px-3 pt-1.5 pb-0.5 text-center text-[0.6875rem] text-muted-foreground tabular-nums">
              Showing {visible.length} of {filtered.length.toLocaleString()} — keep typing.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Shown when the signed-in provider resolves to ZERO IPAs — an explicit
 *  error/empty state (the worklist must never silently send companyId=""). */
export function NoIpaState() {
  return (
    <GateShell>
      <h2 className="text-lg font-bold text-foreground m-0">
        No IPA is configured for your account
      </h2>
      <p role="alert" className="text-sm text-destructive font-medium m-0">
        Your account isn&apos;t scoped to any IPA, so a priority worklist can&apos;t
        be shown. Contact your administrator to have an IPA assigned.
      </p>
    </GateShell>
  );
}

/** Shown while the provider's entitled IPAs are being fetched from PP-API. */
export function ResolvingIpaState() {
  return (
    <GateShell>
      <div
        className="h-8 w-8 rounded-full border-2 border-border border-t-primary animate-spin"
        role="status"
        aria-label="Loading your IPAs"
      />
      <p className="text-sm text-muted-foreground m-0">Loading your worklist…</p>
    </GateShell>
  );
}

/**
 * Shown when PP-API's EntitledIpas call returns a 403 — the signed-in
 * account is authenticated but NOT entitled to a worklist (e.g. a USER
 * MANAGER "view as" peek). Distinct from `NoIpaState` (the fetch SUCCEEDED
 * and returned zero IPAs) and `IpaErrorState` (a transient/network failure,
 * retryable) — this is a fail-closed BACKEND DENIAL, so there is
 * deliberately NO Try-again affordance: retrying would just repeat the same
 * 403. Reuses NoIpaState's shape.
 */
export function IpaForbiddenState() {
  return (
    <GateShell>
      <h2 className="text-lg font-bold text-foreground m-0">
        Your account isn&apos;t entitled to a worklist
      </h2>
      <p role="alert" className="text-sm text-destructive font-medium m-0">
        This account doesn&apos;t have access to a priority worklist. Contact
        your administrator if you believe this is incorrect.
      </p>
    </GateShell>
  );
}

export interface IpaErrorStateProps {
  onRetry: () => void;
}

/** Shown when the PP-API IPA fetch fails (network / 401 / 5xx) — distinct from
 *  NoIpaState (which means the fetch succeeded and returned zero IPAs). */
export function IpaErrorState({ onRetry }: IpaErrorStateProps) {
  return (
    <GateShell>
      <h2 className="text-lg font-bold text-foreground m-0">
        Couldn&apos;t load your worklist
      </h2>
      <p role="alert" className="text-sm text-destructive font-medium m-0">
        We couldn&apos;t reach Provider Portal to load the IPAs your worklist is
        scoped to. Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        Try again
      </button>
    </GateShell>
  );
}
